import { beginWrite, finishWrite, reportUncertainWrite } from './writeOutcome'

// Only requests rejected with HTTP 401 may resume after verified same-user login.
const tokenEndpoint = config => /(?:^|\/)token(?:\/(?:refresh|verify))?\/?$/.test(
  (config.url || '').split('?')[0])

export function tokenUserId(token) {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return String(JSON.parse(atob(part.padEnd(Math.ceil(part.length / 4) * 4, '='))).user_id ?? '')
  } catch { return '' }
}

// Class instances retain identity when Axios merges configs during a retry.
class Deferred {
  constructor() {
    this.promise = new Promise((resolve, reject) => { this.resolve = resolve; this.reject = reject })
    this.promise.catch(() => {})
  }
}
const writeKey = config => {
  if (!['post', 'put', 'patch', 'delete'].includes(config.method)) return null
  try {
    const body = typeof config.data === 'string' ? JSON.parse(config.data) : config.data
    if (body != null && ![Object.prototype, Array.prototype].includes(Object.getPrototypeOf(body))) return null
    return JSON.stringify([config.method, config.url, config.params, body])
  } catch { return null }
}

export function createSessionRecovery({ api, tokens, store, channel, router }) {
  let refreshPromise = null
  let loginPromise = null
  let generation = 0
  let page = 0
  let disposed = false
  const pending = new Set()
  const writes = new Map()
  const paused = () => store.state.auth.reauthRequired
  const owner = () => String(store.state.app.account.profile?.id ?? tokenUserId(tokens.getLocalAccessToken()))
  const sessionError = config => Object.assign(new Error('Требуется повторный вход'), {
    code: 'SESSION_REAUTH_REQUIRED', config,
    response: { status: 401, data: { detail: 'Требуется повторный вход' } },
  })
  const cancelled = config => Object.assign(new Error('Ожидающий запрос отменён при смене страницы'), {
    code: 'ERR_CANCELED', __CANCEL__: true, config,
  })
  const updatePending = () => store.commit('auth/setRecoveryPendingCount', pending.size)
  const settleWrite = (config, response, error) => {
    const flight = config?._sessionWrite
    if (!flight) return
    if (writes.get(flight.key) === flight) writes.delete(flight.key)
    if (error) flight.reject(error)
    else flight.resolve(response)
  }
  const cancelPending = () => {
    for (const item of [...pending]) item.cancel()
  }
  const removeRouteHook = router?.afterEach((to, from, failure) => {
    if (!failure && to.fullPath !== from.fullPath) { page++; cancelPending() }
  })
  const unsubscribe = store.subscribe?.(mutation => {
    if (mutation.type === 'auth/logout') { generation++; cancelPending() }
  })
  const requireLogin = (broadcast = true) => {
    if (!store.state.auth.status.loggedIn || disposed) return
    if (!paused()) {
      generation++
      store.commit('auth/requireReauth', owner())
      if (broadcast) channel.postMessage({ type: 'REAUTH_REQUIRED' })
    }
  }
  const waitForLogin = config => {
    if (disposed || config._sessionPage !== page || config.signal?.aborted) return Promise.reject(cancelled(config))
    const deferred = new Deferred()
    const item = {
      config,
      cancel() { cleanup(); deferred.reject(cancelled(config)) },
      resume() {
        cleanup()
        if (config._sessionPage !== page || config.signal?.aborted || config._sessionUser !== owner()) {
          deferred.reject(cancelled(config)); return
        }
        config._reauthRetry = true
        config._retry = true
        deferred.resolve(api(config))
      },
    }
    const cleanup = () => {
      pending.delete(item)
      config.signal?.removeEventListener('abort', item.cancel)
      updatePending()
    }
    pending.add(item)
    config.signal?.addEventListener('abort', item.cancel, { once: true })
    updatePending()
    // A 401 can arrive after another tab has already restored this session.
    if (!paused()) item.resume()
    return deferred.promise
  }
  const acceptLogin = (data, profile) => {
    if (!profile?.id || String(profile.id) !== store.state.auth.reauthUserId) {
      throw Object.assign(new Error('Войдите под прежней учётной записью.'), { code: 'SESSION_USER_MISMATCH' })
    }
    generation++
    tokens.setUser(data)
    store.commit('app/setProfile', profile)
    store.commit('auth/loginSuccess', data)
    for (const item of [...pending]) item.resume()
  }
  async function reauthenticate(credentials) {
    if (loginPromise) return loginPromise
    loginPromise = (async () => {
      const { data } = await api.post('token/', credentials, { _skipSessionRecovery: true })
      const { data: profile } = await api.get('profile/', {
        _skipSessionRecovery: true, headers: { Authorization: 'Bearer ' + data.access },
      })
      acceptLogin(data, profile)
      channel.postMessage({ type: 'SESSION_RESTORED' })
    })()
    try { return await loginPromise } finally { loginPromise = null }
  }
  const requestId = api.interceptors.request.use(async config => {
    if (config._skipSessionRecovery || tokenEndpoint(config)) return config
    if (disposed) throw cancelled(config)
    config._sessionPage ??= page
    config._sessionUser ??= owner()
    if (config._sessionPage !== page || config.signal?.aborted) throw cancelled(config)
    if (!config._sessionWrite) {
      const key = writeKey(config)
      if (key) {
        if (writes.has(key)) {
          throw Object.assign(new Error('Запрос уже выполняется'), {
            code: 'SESSION_DUPLICATE_REQUEST', config, flight: writes.get(key),
          })
        }
        // A new write during the login dialog has not received a server 401.
        if (paused()) throw sessionError(config)
        const flight = new Deferred()
        flight.key = key
        writes.set(key, flight)
        config._sessionWrite = flight
      }
    }
    if (paused()) {
      if (!['get', 'head'].includes(config.method)) throw sessionError(config)
      // Safe reads may wait before being sent; never enqueue an unconfirmed write.
      return Promise.reject(Object.assign(sessionError(config), { code: 'SESSION_WAIT_READ' }))
    }
    const token = tokens.getLocalAccessToken()
    if (token && owner() && tokenUserId(token) !== owner()) {
      requireLogin()
      throw sessionError(config)
    }
    if (token) config.headers.Authorization = 'Bearer ' + token
    config._sessionAccess = token
    config._sessionGeneration ??= generation
    beginWrite(config)
    return config
  })
  async function recover(err) {
    const config = err.config
    if (err.code === 'SESSION_DUPLICATE_REQUEST') return err.flight.promise
    if (err.code === 'SESSION_WAIT_READ') return waitForLogin(config)
    if (!config || config._skipSessionRecovery || tokenEndpoint(config) ||
        err.code === 'SESSION_REAUTH_REQUIRED' || err.__CANCEL__ || err.response?.status !== 401) throw err
    if (!store.state.auth.status.loggedIn) throw err
    if (config._sessionPage !== page || config.signal?.aborted) throw cancelled(config)
    // At most one replay following manual reauthentication. No retry loop.
    if (config._reauthRetry) { requireLogin(); throw err }
    if (paused()) return waitForLogin(config)
    if (config._sessionGeneration !== generation) return waitForLogin(config)
    if (config._retry) { requireLogin(); return waitForLogin(config) }
    config._retry = true
    if (config._sessionAccess !== tokens.getLocalAccessToken()) return api(config)
    if (!refreshPromise) {
      const refreshToken = tokens.getLocalRefreshToken()
      if (!refreshToken) { requireLogin(); return waitForLogin(config) }
      const started = generation
      refreshPromise = api.post('token/refresh/', { refresh: refreshToken })
        .then(({ data }) => {
          if (generation !== started || paused()) return
          tokens.setUser({ access: data.access, refresh: data.refresh || refreshToken })
          store.dispatch('auth/refreshToken', data.access)
        })
        .catch(error => {
          if (generation !== started) return
          if ([400, 401].includes(error.response?.status) &&
              tokens.getLocalRefreshToken() !== refreshToken && tokenUserId(tokens.getLocalAccessToken()) === owner()) return
          if ([400, 401].includes(error.response?.status)) requireLogin()
          throw error
        })
        .finally(() => { refreshPromise = null })
    }
    try { await refreshPromise } catch (error) {
      if (paused()) return waitForLogin(config)
      throw error // Never replay network errors or 5xx on writes.
    }
    if (paused() || config._sessionGeneration !== generation) return waitForLogin(config)
    return api(config)
  }
  const responseId = api.interceptors.response.use(response => {
    finishWrite(response.config)
    settleWrite(response.config, response)
    return response
  }, async err => {
    try { return await recover(err) } catch (error) {
      if (['get', 'head'].includes(err.config?.method) && !tokenEndpoint(err.config) && error !== err) {
        error._connectionOriginalConfig ??= err.config
      }
      reportUncertainWrite(err, error)
      finishWrite(err.config)
      settleWrite(err.config, null, error)
      throw error
    }
  })
  const onMessage = async ({ data }) => {
    if (data.type === 'REAUTH_REQUIRED') requireLogin(false)
    if (data.type === 'SESSION_RESTORED' && paused()) {
      const access = tokens.getLocalAccessToken()
      if (tokenUserId(access) !== store.state.auth.reauthUserId) return
      try {
        const { data: profile } = await api.get('profile/', {
          _skipSessionRecovery: true, headers: { Authorization: 'Bearer ' + access },
        })
        acceptLogin({ access, refresh: tokens.getLocalRefreshToken() }, profile)
      } catch { /* Keep requests waiting until this tab verifies the same user. */ }
    }
  }
  channel.addEventListener('message', onMessage)
  return {
    reauthenticate, cancelPending,
    dispose() {
      disposed = true
      cancelPending()
      unsubscribe?.()
      removeRouteHook?.()
      channel.removeEventListener('message', onMessage)
      channel.close()
      api.interceptors.request.eject(requestId)
      api.interceptors.response.eject(responseId)
    },
  }
}
